import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { paymentGatewayApi } from '@/services/paymentGatewayApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { LoadingState, ErrorState } from '@/components/common';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Save, Trash2, Eye, EyeOff } from 'lucide-react';
import type { PaymentGateway, PaymentGatewayConfig, PaymentGatewayConfigRequest, PaymentMethod, PaymentMethodsResponse } from '@/types/paymentGateway';

// Zod schema for gateway configuration
const gatewayConfigSchema = z.object({
  gatewayType: z.enum([
    'MOCK', 'STRIPE', 'PAYPAL', 'RAZORPAY', 'SQUARE', 'ADYEN', 'BRAINTREE',
    'CHECKOUT_COM', 'WORLDPAY', 'PAYU', 'MOLLIE', 'KLARNA', 'APPLE_PAY',
    'GOOGLE_PAY', 'AMAZON_PAY', 'ALIPAY', 'WECHAT_PAY',
  ]),
  merchantName: z.string().min(1, 'Merchant name is required').max(100),
  apiEndpoint: z.string().url('Invalid URL').optional().or(z.literal('')),
  publicKey: z.string().optional(),
  secretKey: z.string().optional(),
  webhookUrl: z.string().url('Invalid URL').optional().or(z.literal('')),
  webhookSecret: z.string().optional(),
  sandboxMode: z.boolean().optional(),
  enabled: z.boolean(),
  priority: z.number().int().min(0).optional(),
  supportedCurrencies: z.array(z.string().regex(/^[A-Z]{3}$/)).optional(),
  supportedPaymentMethods: z.array(z.enum([
    'CREDIT_CARD', 'DEBIT_CARD', 'BANK_TRANSFER', 'E_WALLET',
    'CRYPTOCURRENCY', 'BANK_ACCOUNT', 'DIGITAL_WALLET', 'PREPAID_CARD', 'MOBILE_PAYMENT'
  ])).optional(),
});

type GatewayConfigFormData = z.infer<typeof gatewayConfigSchema>;

const PAYMENT_GATEWAYS: PaymentGateway[] = [
  'MOCK', 'STRIPE', 'PAYPAL', 'RAZORPAY', 'SQUARE', 'ADYEN', 'BRAINTREE',
  'CHECKOUT_COM', 'WORLDPAY', 'PAYU', 'MOLLIE', 'KLARNA', 'APPLE_PAY',
  'GOOGLE_PAY', 'AMAZON_PAY', 'ALIPAY', 'WECHAT_PAY',
];

const GATEWAY_NAMES: Record<PaymentGateway, string> = {
  MOCK: 'Mock Gateway',
  STRIPE: 'Stripe',
  PAYPAL: 'PayPal',
  RAZORPAY: 'Razorpay',
  SQUARE: 'Square',
  ADYEN: 'Adyen',
  BRAINTREE: 'Braintree',
  CHECKOUT_COM: 'Checkout.com',
  WORLDPAY: 'Worldpay',
  PAYU: 'PayU',
  MOLLIE: 'Mollie',
  KLARNA: 'Klarna',
  APPLE_PAY: 'Apple Pay',
  GOOGLE_PAY: 'Google Pay',
  AMAZON_PAY: 'Amazon Pay',
  ALIPAY: 'Alipay',
  WECHAT_PAY: 'WeChat Pay',
};

export const PaymentGatewayFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();
  const isEdit = !!id;
  const gatewayFromQuery = searchParams.get('gateway') as PaymentGateway | null;
  const [showPublicKey, setShowPublicKey] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  // Track original keys (for preserving on update)
  const [originalPublicKey, setOriginalPublicKey] = useState<string | null>(null);
  const [originalSecretKey, setOriginalSecretKey] = useState<string | null>(null);
  // Track if user wants to change keys (for edit mode)
  const [changePublicKey, setChangePublicKey] = useState(false);
  const [changeSecretKey, setChangeSecretKey] = useState(false);
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<PaymentMethod[]>([]);

  const canDelete = hasPermission('delete', 'paymentGateway');

  // Fetch payment methods from API
  const {
    data: paymentMethodsResponse,
    isLoading: isLoadingPaymentMethods,
  } = useQuery<PaymentMethodsResponse>({
    queryKey: ['paymentMethods'],
    queryFn: async () => {
      return await paymentGatewayApi.getPaymentMethods();
    },
  });

  const form = useForm<GatewayConfigFormData>({
    resolver: zodResolver(gatewayConfigSchema),
    defaultValues: {
      gatewayType: (gatewayFromQuery || 'STRIPE') as PaymentGateway,
      merchantName: gatewayFromQuery ? GATEWAY_NAMES[gatewayFromQuery] || '' : '',
      apiEndpoint: '',
      publicKey: '',
      secretKey: '',
      webhookUrl: '',
      webhookSecret: '',
      sandboxMode: true,
      enabled: true,
      priority: 1,
      supportedCurrencies: [],
      supportedPaymentMethods: [],
    },
  });

  // Fetch gateway configuration for edit mode
  const {
    data: gatewayConfig,
    isLoading: isLoadingConfig,
    error: configError,
  } = useEntity<PaymentGatewayConfig>({
    fetchFn: (configId: string) => paymentGatewayApi.getConfigById(configId),
    entityId: id,
    entityName: 'paymentGatewayConfig',
    enabled: isEdit && !!id,
    onSuccess: (data) => {
      // Map backend response to form fields (support both new and legacy field names)
      const gatewayType = data.gatewayType || data.gatewayCode;
      const merchantName = data.merchantName || data.displayName || '';
      // Note: Keys are masked in API response (publicKeyMasked), so we can't pre-fill them
      // Users will need to re-enter keys if they want to update them
      const sandboxMode = data.sandboxMode ?? data.testMode ?? false;
      
      // Store original keys for preserving on update (backend now returns actual keys)
      const originalPublicKeyValue = data.publicKey || null;
      const originalSecretKeyValue = data.secretKey || null;
      setOriginalPublicKey(originalPublicKeyValue);
      setOriginalSecretKey(originalSecretKeyValue);
      
      // Use payment methods from the API data directly (no filtering needed since API returns valid enum values)
      const validPaymentMethods = (data.supportedPaymentMethods || []) as PaymentMethod[];
      
      form.reset({
        gatewayType: gatewayType!,
        merchantName,
        apiEndpoint: data.apiEndpoint || '',
        publicKey: '', // Don't pre-fill keys in form (show masked value instead)
        secretKey: '', // Don't pre-fill keys in form (show masked value instead)
        webhookUrl: data.webhookUrl || '',
        webhookSecret: data.webhookSecret || '',
        sandboxMode,
        enabled: data.enabled,
        priority: data.priority ?? 1,
        supportedCurrencies: data.supportedCurrencies || [],
        supportedPaymentMethods: validPaymentMethods,
      });
      setSelectedCurrencies(data.supportedCurrencies || []);
      setSelectedPaymentMethods(validPaymentMethods);
      // Reset change flags when config loads
      setChangePublicKey(false);
      setChangeSecretKey(false);
    },
  });

  // Create mutation (onboard)
  const createMutation = useMutation({
    mutationFn: async (data: GatewayConfigFormData) => {
      const payload: PaymentGatewayConfigRequest = {
        gatewayType: data.gatewayType,
        merchantName: data.merchantName,
        apiEndpoint: data.apiEndpoint || undefined,
        publicKey: data.publicKey || undefined,
        secretKey: data.secretKey || undefined,
        webhookUrl: data.webhookUrl || undefined,
        webhookSecret: data.webhookSecret || undefined,
        sandboxMode: data.sandboxMode,
        enabled: data.enabled,
        priority: data.priority,
        supportedCurrencies: data.supportedCurrencies && data.supportedCurrencies.length > 0 ? data.supportedCurrencies : undefined,
        supportedPaymentMethods: data.supportedPaymentMethods && data.supportedPaymentMethods.length > 0 ? data.supportedPaymentMethods : undefined,
      };
      return await paymentGatewayApi.onboard(payload);
    },
    entityName: 'paymentGatewayConfig',
    operation: 'create',
    onSuccess: () => {
      navigate('/admin/payment-gateways');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: GatewayConfigFormData) => {
      if (!id || !gatewayConfig) throw new Error('Gateway Configuration ID and config are required');
      
      // The form is pre-populated with original values via form.reset(), so data contains the current form values
      // For unchanged fields, data will have the original values (from form.reset())
      // For changed fields, data will have the new values
      // For keys (publicKey, secretKey), they're empty strings because API returns masked values
      const payload: PaymentGatewayConfigRequest = {
        gatewayType: data.gatewayType,
        merchantName: data.merchantName,
        // Use form value if it's a non-empty string, otherwise use original from gatewayConfig
        apiEndpoint: data.apiEndpoint?.trim() || gatewayConfig.apiEndpoint || undefined,
        webhookUrl: data.webhookUrl?.trim() || gatewayConfig.webhookUrl || undefined,
        webhookSecret: data.webhookSecret?.trim() || gatewayConfig.webhookSecret || undefined,
        // Keys: Always required. Send new values if user changed them, otherwise use original keys from gatewayConfig
        // Backend returns actual keys, so we can preserve them if user didn't change
        publicKey: (changePublicKey && data.publicKey?.trim()) ? data.publicKey.trim() : (gatewayConfig.publicKey || originalPublicKey || ''),
        secretKey: (changeSecretKey && data.secretKey?.trim()) ? data.secretKey.trim() : (gatewayConfig.secretKey || originalSecretKey || ''),
        // Use form values directly (they're pre-populated with originals if unchanged)
        sandboxMode: data.sandboxMode,
        enabled: data.enabled,
        priority: data.priority,
        // Use form field values (they're synced with state via form.setValue)
        supportedCurrencies: data.supportedCurrencies && data.supportedCurrencies.length > 0 ? data.supportedCurrencies : (gatewayConfig.supportedCurrencies || undefined),
        supportedPaymentMethods: data.supportedPaymentMethods && data.supportedPaymentMethods.length > 0 ? data.supportedPaymentMethods : (gatewayConfig.supportedPaymentMethods || undefined),
      };
      
      return await paymentGatewayApi.updateConfig(id, payload);
    },
    entityName: 'paymentGatewayConfig',
    operation: 'update',
    onSuccess: () => {
      navigate('/admin/payment-gateways');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Gateway Configuration ID is required');
      await paymentGatewayApi.deleteConfig(id);
    },
    entityName: 'paymentGatewayConfig',
    operation: 'delete',
    onSuccess: () => {
      navigate('/admin/payment-gateways');
    },
  });

  const onSubmit = async (data: GatewayConfigFormData) => {
    if (isEdit) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this gateway configuration?')) return;
    await deleteMutation.mutateAsync();
  };

  const toggleCurrency = (currency: string) => {
    const newCurrencies = selectedCurrencies.includes(currency)
      ? selectedCurrencies.filter((c) => c !== currency)
      : [...selectedCurrencies, currency];
    setSelectedCurrencies(newCurrencies);
    // Sync with form field
    form.setValue('supportedCurrencies', newCurrencies);
  };

  // Derive available payment methods from API
  const availablePaymentMethods = useMemo(() => {
    if (!paymentMethodsResponse?.paymentMethods) return [];
    return paymentMethodsResponse.paymentMethods
      .filter((pm) => pm.available)
      .sort((a, b) => (a.displayPriority || 999) - (b.displayPriority || 999));
  }, [paymentMethodsResponse]);

  // Derive available currencies from selected payment methods
  const availableCurrencies = useMemo(() => {
    if (!paymentMethodsResponse?.paymentMethods) return [];
    if (selectedPaymentMethods.length === 0) {
      // If no payment methods selected, show all currencies from API
      return paymentMethodsResponse.supportedCurrencies || [];
    }
    // Get currencies from selected payment methods
    const currencySet = new Set<string>();
    selectedPaymentMethods.forEach((methodType) => {
      const methodInfo = paymentMethodsResponse.paymentMethods.find(
        (pm) => pm.methodType === methodType
      );
      if (methodInfo?.supportedCurrencies) {
        methodInfo.supportedCurrencies.forEach((curr) => currencySet.add(curr));
      }
    });
    return Array.from(currencySet).sort();
  }, [paymentMethodsResponse, selectedPaymentMethods]);

  const togglePaymentMethod = (method: PaymentMethod) => {
    const newMethods = selectedPaymentMethods.includes(method)
      ? selectedPaymentMethods.filter((m) => m !== method)
      : [...selectedPaymentMethods, method];
    setSelectedPaymentMethods(newMethods);
    // Sync with form field
    form.setValue('supportedPaymentMethods', newMethods);
    
    // When payment methods change, update available currencies
    // The useMemo above will automatically recalculate
  };

  const isLoading = isLoadingConfig;
  const mutation = isEdit ? updateMutation : createMutation;

  // In edit mode, show loading state while fetching
  if (isEdit && isLoading) {
    return <LoadingState message="Loading gateway configuration..." />;
  }

  // Show error if there's an error (only after loading completes)
  if (isEdit && configError) {
    return <ErrorState error={configError} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/payment-gateways">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEdit ? 'Edit Gateway Configuration' : 'Create Gateway Configuration'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isEdit ? 'Update gateway configuration' : 'Configure a new payment gateway'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {isEdit && canDelete && (
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isLoading}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, (errors) => {
          console.error('Form validation errors:', errors);
          // Log detailed validation errors for debugging
          if (errors.supportedPaymentMethods && Array.isArray(errors.supportedPaymentMethods)) {
            console.error('supportedPaymentMethods validation error:', JSON.stringify(errors.supportedPaymentMethods, null, 2));
            errors.supportedPaymentMethods.forEach((err, index) => {
              console.error(`Error ${index}:`, err);
            });
          }
        })} className="space-y-6">
          <Tabs defaultValue="basic" className="space-y-6">
            <TabsList>
              <TabsTrigger value="basic">Basic Information</TabsTrigger>
              <TabsTrigger value="credentials">Credentials</TabsTrigger>
              <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
              <TabsTrigger value="geographical">Supported Options</TabsTrigger>
            </TabsList>

            {/* Basic Information Tab */}
            <TabsContent value="basic" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Basic Information</CardTitle>
                  <CardDescription>Gateway identification and basic settings</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <FormField
                    control={form.control}
                    name="gatewayType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Gateway Type</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          disabled={isEdit}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select gateway" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {PAYMENT_GATEWAYS.map((gw) => (
                              <SelectItem key={gw} value={gw}>
                                {GATEWAY_NAMES[gw]} ({gw})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Select the payment gateway to configure. This cannot be changed after creation.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="merchantName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Merchant Name</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., My Company" {...field} />
                        </FormControl>
                        <FormDescription>
                          The merchant/company name for this gateway configuration
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="apiEndpoint"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>API Endpoint</FormLabel>
                        <FormControl>
                          <Input placeholder="https://api.stripe.com" {...field} />
                        </FormControl>
                        <FormDescription>
                          The API endpoint URL for the payment gateway
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="enabled"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">Enabled</FormLabel>
                          <FormDescription>
                            Enable or disable this gateway configuration
                          </FormDescription>
                        </div>
                        <FormControl>
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={field.onChange}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="sandboxMode"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">Sandbox Mode</FormLabel>
                          <FormDescription>
                            Use test/sandbox environment instead of production
                          </FormDescription>
                        </div>
                        <FormControl>
                          <input
                            type="checkbox"
                            checked={field.value ?? false}
                            onChange={field.onChange}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="priority"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Priority</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="1"
                            {...field}
                            onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 0)}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <FormDescription>
                          Priority/order for gateway selection (lower numbers have higher priority)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Credentials Tab */}
            <TabsContent value="credentials" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>API Credentials</CardTitle>
                  <CardDescription>Gateway API keys and secrets</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <FormField
                    control={form.control}
                    name="publicKey"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Public Key</FormLabel>
                        {isEdit && !changePublicKey && gatewayConfig?.publicKeyMasked ? (
                          <div className="space-y-2">
                            <div className="flex gap-2">
                              <Input
                                type="text"
                                value={gatewayConfig.publicKeyMasked}
                                disabled
                                className="font-mono"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={() => setShowPublicKey(!showPublicKey)}
                              >
                                {showPublicKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </Button>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setChangePublicKey(true);
                                field.onChange('');
                              }}
                            >
                              Change Public Key
                            </Button>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <FormControl>
                              <Input
                                type={showPublicKey ? 'text' : 'password'}
                                placeholder={isEdit ? 'Enter new public key' : 'Enter public key'}
                                {...field}
                              />
                            </FormControl>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => setShowPublicKey(!showPublicKey)}
                            >
                              {showPublicKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                            {isEdit && changePublicKey && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setChangePublicKey(false);
                                  field.onChange('');
                                }}
                              >
                                Cancel
                              </Button>
                            )}
                          </div>
                        )}
                        <FormDescription>
                          {isEdit && !changePublicKey
                            ? 'Current public key. Click "Change Public Key" to update it.'
                            : isEdit
                            ? 'Enter new public key to update it.'
                            : 'Your gateway public API key.'}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="secretKey"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Secret Key</FormLabel>
                        {isEdit && !changeSecretKey ? (
                          <div className="space-y-2">
                            <div className="flex gap-2">
                              <Input
                                type="password"
                                value="••••••••••••••••"
                                disabled
                                className="font-mono"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={() => setShowSecretKey(!showSecretKey)}
                                disabled
                              >
                                <EyeOff className="h-4 w-4" />
                              </Button>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setChangeSecretKey(true);
                                field.onChange('');
                              }}
                            >
                              Change Secret Key
                            </Button>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <FormControl>
                              <Input
                                type={showSecretKey ? 'text' : 'password'}
                                placeholder={isEdit ? 'Enter new secret key' : 'Enter secret key'}
                                {...field}
                              />
                            </FormControl>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => setShowSecretKey(!showSecretKey)}
                            >
                              {showSecretKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                            {isEdit && changeSecretKey && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setChangeSecretKey(false);
                                  field.onChange('');
                                }}
                              >
                                Cancel
                              </Button>
                            )}
                          </div>
                        )}
                        <FormDescription>
                          {isEdit && !changeSecretKey
                            ? 'Current secret key is stored securely. Click "Change Secret Key" to update it.'
                            : isEdit
                            ? 'Enter new secret key to update it. Keep this secure and never share it.'
                            : 'Your gateway secret API key. Keep this secure and never share it.'}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Webhooks Tab */}
            <TabsContent value="webhooks" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Webhook Configuration</CardTitle>
                  <CardDescription>Webhook URL and secret for receiving gateway events</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <FormField
                    control={form.control}
                    name="webhookUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Webhook URL</FormLabel>
                        <FormControl>
                          <Input
                            type="url"
                            placeholder="https://your-domain.com/api/webhooks/gateway"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          URL where gateway webhooks will be sent
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="webhookSecret"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Webhook Secret</FormLabel>
                        <div className="flex gap-2">
                          <FormControl>
                            <Input
                              type={showWebhookSecret ? 'text' : 'password'}
                              placeholder="Enter webhook secret"
                              {...field}
                            />
                          </FormControl>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                          >
                            {showWebhookSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                        <FormDescription>
                          Secret for verifying webhook signatures
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Support Tab */}
            <TabsContent value="geographical" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Supported Options</CardTitle>
                  <CardDescription>Supported currencies and payment methods</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label>Supported Currencies</Label>
                    {isLoadingPaymentMethods ? (
                      <p className="text-sm text-muted-foreground">Loading currencies...</p>
                    ) : (
                      <>
                        <div className="flex flex-wrap gap-2">
                          {availableCurrencies.map((currency) => (
                            <button
                              key={currency}
                              type="button"
                              onClick={() => toggleCurrency(currency)}
                              className={`px-3 py-1 rounded-md text-sm font-mono border ${
                                selectedCurrencies.includes(currency)
                                  ? 'bg-primary text-primary-foreground border-primary'
                                  : 'bg-background border-input hover:bg-accent'
                              }`}
                            >
                              {currency}
                            </button>
                          ))}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Selected: {selectedCurrencies.length > 0 ? selectedCurrencies.join(', ') : 'None'}
                        </p>
                      </>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Supported Payment Methods</Label>
                    {isLoadingPaymentMethods ? (
                      <p className="text-sm text-muted-foreground">Loading payment methods...</p>
                    ) : (
                      <>
                        <div className="flex flex-wrap gap-2">
                          {availablePaymentMethods.map((methodInfo) => (
                            <button
                              key={methodInfo.methodType}
                              type="button"
                              onClick={() => togglePaymentMethod(methodInfo.methodType)}
                              className={`px-3 py-1 rounded-md text-sm border ${
                                selectedPaymentMethods.includes(methodInfo.methodType)
                                  ? 'bg-primary text-primary-foreground border-primary'
                                  : 'bg-background border-input hover:bg-accent'
                              }`}
                              title={methodInfo.description}
                            >
                              {methodInfo.displayName || methodInfo.methodType.replace(/_/g, ' ')}
                            </button>
                          ))}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Selected: {selectedPaymentMethods.length > 0 ? selectedPaymentMethods.map(m => {
                            const methodInfo = availablePaymentMethods.find(pm => pm.methodType === m);
                            return methodInfo?.displayName || m.replace(/_/g, ' ');
                          }).join(', ') : 'None'}
                        </p>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" asChild>
              <Link to="/admin/payment-gateways">Cancel</Link>
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {mutation.isLoading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default PaymentGatewayFormPage;
